import React, { useState, useEffect } from 'react';
import { apiFetch } from './api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Status de atualização de marca: backend é pass-through (SEF-32), então o front
// normaliza token conhecido → classe própria; desconhecido/vazio → neutro.
const STATUS_ATUALIZACAO_CONHECIDOS = ['alerta', 'atualizada', 'pendente'];

function classeStatusAtualizacao(status) {
  return STATUS_ATUALIZACAO_CONHECIDOS.includes(status) ? `status-${status}` : 'status-neutro';
}

function rotuloStatusAtualizacao(status) {
  if (!status) {
    return '—';
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function Dashboard({ onNavegar }) {
  const [proximasAcoes, setProximasAcoes] = useState([]);
  const [funilVendas, setFunilVendas] = useState({
    prospectos: 0,
    leads: 0,
    qualificados: 0,
    apresentacao: 0,
    negociacao: 0,
    fechamento: 0,
    contrato: 0
  });
  const [resumoFinanceiro, setResumoFinanceiro] = useState({
    receitaMensal: 0,
    receitaAnual: 0,
    previsaoMes: 0,
    contasReceber: 0
  });
  const [atualizacoesMarcas, setAtualizacoesMarcas] = useState([]);
  const [dataCalendario, setDataCalendario] = useState(new Date());
  const [conversao, setConversao] = useState([]);
  const [periodoFunil, setPeriodoFunil] = useState({ dataInicio: '', dataFim: '' });

  useEffect(() => {
    carregarDados();
  }, []);

  // Taxas de conversão entre etapas do funil (SEF-33 US-003), com filtro de
  // período opcional (US-002). Recarrega quando o período muda.
  useEffect(() => {
    let ativo = true;
    const params = new URLSearchParams();
    if (periodoFunil.dataInicio) params.set('dataInicio', periodoFunil.dataInicio);
    if (periodoFunil.dataFim) params.set('dataFim', periodoFunil.dataFim);
    const qs = params.toString();
    apiFetch(`/api/dashboard/funil/conversao${qs ? `?${qs}` : ''}`)
      .then((d) => {
        if (ativo) setConversao(d?.etapas ?? []);
      })
      .catch(() => {
        if (ativo) setConversao([]);
      });
    return () => {
      ativo = false;
    };
  }, [periodoFunil]);

  // Foto atual do funil (SEF-33). O período do card filtra tanto a foto atual
  // quanto a conversão. Os demais widgets seguem mock (Próximas Ações = SEF-34,
  // Resumo Financeiro = SEF-35).
  useEffect(() => {
    let ativo = true;
    const params = new URLSearchParams();
    if (periodoFunil.dataInicio) params.set('dataInicio', periodoFunil.dataInicio);
    if (periodoFunil.dataFim) params.set('dataFim', periodoFunil.dataFim);
    const qs = params.toString();
    apiFetch(`/api/dashboard/funil${qs ? `?${qs}` : ''}`)
      .then((d) => {
        if (!ativo || !d) return;
        setFunilVendas({
          prospectos: d.prospectos ?? 0,
          leads: d.leads ?? 0,
          qualificados: d.qualificados ?? 0,
          apresentacao: d.apresentacao ?? 0,
          negociacao: d.negociacao ?? 0,
          fechamento: d.fechamento ?? 0,
          contrato: d.contrato ?? 0,
        });
      })
      .catch(() => {
        // mantém os zeros iniciais se a API falhar
      });
    return () => {
      ativo = false;
    };
  }, [periodoFunil]);

  // Próximas ações reais (SEF-34). Resiliente: se a API falhar, o widget fica
  // vazio sem quebrar o restante do dashboard.
  useEffect(() => {
    let ativo = true;
    apiFetch('/api/dashboard/acoes')
      .then((d) => {
        if (ativo) setProximasAcoes(d?.acoes ?? []);
      })
      .catch(() => {
        // sem ações se a API falhar
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Atualizações de marcas reais (SEF-32). Resiliente: widget vazio se a API falhar.
  useEffect(() => {
    let ativo = true;
    apiFetch('/api/dashboard/marcas/atualizacoes')
      .then((d) => {
        if (ativo) setAtualizacoesMarcas(d?.atualizacoes ?? []);
      })
      .catch(() => {
        // sem atualizações se a API falhar
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Resumo Financeiro (SEF-35 / PRD-27): KPIs reais de GET /api/dashboard/financeiro (não mais hardcoded).
  const carregarDados = () => {
    apiFetch('/api/dashboard/financeiro')
      .then((dados) => {
        setResumoFinanceiro({
          receitaMensal: dados.receitaMensal || 0,
          receitaAnual: dados.receitaAnual || 0,
          previsaoMes: dados.previsaoMes || 0,
          contasReceber: dados.contasReceber || 0
        });
      })
      .catch(() => {
        // mantém os zeros do estado inicial se a API falhar
      });
  };

  // Rótulos com acento por etapa (o key cru perde os acentos do pt-BR).
  const ROTULOS_FUNIL = {
    prospectos: 'Prospectos',
    leads: 'Leads',
    qualificados: 'Qualificados',
    apresentacao: 'Apresentação',
    negociacao: 'Negociação',
    fechamento: 'Fechamento',
    contrato: 'Contrato',
  };

  // Componente do Funil
  const FunilVendas = ({ dados }) => {
    const maximo = Math.max(...Object.values(dados));
    const total = Object.values(dados).reduce((acc, curr) => acc + curr, 0);
    const entries = Object.entries(dados);
    
    return (
      <div className="funil-wrapper">
        {entries.map(([key, value], index) => {
          const porcentagem = total > 0 ? (value / total * 100).toFixed(1) : '0.0';
          const largura = maximo > 0 ? calcularLarguraFunil(value, maximo) : 0;
          
          return (
            <div key={key} className="funil-item-wrapper">
              <div className="funil-label">
                <span className="funil-texto">{ROTULOS_FUNIL[key] || (key.charAt(0).toUpperCase() + key.slice(1))}</span>
                <span className="funil-valor">{value}</span>
              </div>
              <div className="funil-barra-container">
                <div 
                  className="funil-barra"
                  style={{
                    width: `${largura}%`,
                    marginLeft: `${(100 - largura) / 2}%`
                  }}
                >
                  <span className="funil-porcentagem">{porcentagem}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Ícone de Atualização
  const IconeAtualizacao = ({ tipo }) => {
    switch (tipo) {
      case 'revista':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case 'registro':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'monitoramento':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'relatorio':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const calcularLarguraFunil = (valor, maximo) => {
    return (valor / maximo) * 100;
  };

  // Dados para o gráfico financeiro
  const financeiroData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Receita Mensal',
        data: [45000, 48000, 50000, 52000, 54000, 58000],
        borderColor: 'rgba(147, 51, 234, 1)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        fill: true
      }
    ]
  };

  return (
    <div className="dashboard-container">
      <h1>Dashboard</h1>

      {/* Atalhos rápidos (UI/UX-002): ações primárias a 1 clique. Cor por modo mental —
          gestão = --sefira-primary; captação (lead) = --lead-color (governança de cores). */}
      <div className="dashboard-atalhos">
        <button type="button" className="atalho-card" onClick={() => onNavegar && onNavegar('clientes')}>
          <i className="fa-solid fa-user-plus"></i>
          <span>Novo cliente</span>
        </button>
        <button type="button" className="atalho-card" onClick={() => onNavegar && onNavegar('marcas')}>
          <i className="fa-solid fa-magnifying-glass"></i>
          <span>Verificar marca</span>
        </button>
        <button type="button" className="atalho-card" onClick={() => onNavegar && onNavegar('financeiro')}>
          <i className="fa-solid fa-circle-dollar-to-slot"></i>
          <span>Adicionar receita</span>
        </button>
        <button type="button" className="atalho-card atalho-card--lead" onClick={() => onNavegar && onNavegar('leads')}>
          <i className="fa-solid fa-bullseye"></i>
          <span>Buscar lead</span>
        </button>
      </div>

      <div className="dashboard-grid">
        {/* Calendário e Próximas Ações */}
        <div className="dashboard-card calendario-card">
          <h2>Calendário e Próximas Ações</h2>
          <div className="calendario-container">
            <div className="calendario">
              <Calendar
                onChange={setDataCalendario}
                value={dataCalendario}
                locale="pt-BR"
              />
            </div>
            <div className="acoes-lista">
              <h3>Ações do Dia</h3>
              {proximasAcoes.length === 0 && (
                <p className="acoes-vazio">Sem próximas ações.</p>
              )}
              {proximasAcoes.map(acao => {
                const podeAbrir = Boolean(onNavegar && acao.clienteId);
                const abrir = () => onNavegar('detalhes-cliente', { clienteId: acao.clienteId });
                return (
                  <div
                    key={acao.id}
                    className={`acao-item status-${acao.status}${podeAbrir ? ' acao-item--clicavel' : ''}`}
                    onClick={podeAbrir ? abrir : undefined}
                    onKeyDown={podeAbrir ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        abrir();
                      }
                    } : undefined}
                    role={podeAbrir ? 'button' : undefined}
                    tabIndex={podeAbrir ? 0 : undefined}
                  >
                    <div className="acao-data">
                      <span className="hora">{acao.hora}</span>
                    </div>
                    <div className="acao-info">
                      <strong>{acao.cliente}</strong>
                      <p>{acao.acao}</p>
                    </div>
                    <span className="acao-status">{acao.status}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Funil de Vendas */}
        <div className="dashboard-card funil-card">
          <h2>Funil de Vendas</h2>
          <div className="funil-container">
            <FunilVendas dados={funilVendas} />
            <div className="funil-total">
              <span className="funil-total-label">Total de Oportunidades</span>
              <span className="funil-total-valor">{Object.values(funilVendas).reduce((acc, curr) => acc + curr, 0)}</span>
            </div>
            <div className="funil-conversao">
              <div className="funil-conversao-cabecalho">
                <h3 className="funil-conversao-titulo">Taxas de Conversão</h3>
                <div className="funil-conversao-periodo">
                  <label>
                    De
                    <input
                      type="date"
                      aria-label="Data início"
                      value={periodoFunil.dataInicio}
                      onChange={(e) => setPeriodoFunil((p) => ({ ...p, dataInicio: e.target.value }))}
                    />
                  </label>
                  <label>
                    Até
                    <input
                      type="date"
                      aria-label="Data fim"
                      value={periodoFunil.dataFim}
                      onChange={(e) => setPeriodoFunil((p) => ({ ...p, dataFim: e.target.value }))}
                    />
                  </label>
                </div>
              </div>
              {conversao.length > 0 ? (
                <ul className="funil-conversao-lista">
                  {conversao.map((t, i) => (
                    <li key={`${t.de}-${t.para}-${i}`} className="funil-conversao-item">
                      <span className="funil-conversao-etapas">
                        {(ROTULOS_FUNIL[t.de] || t.de)} → {(ROTULOS_FUNIL[t.para] || t.para)}
                      </span>
                      <span className="funil-conversao-taxa">{(t.taxa * 100).toFixed(0)}%</span>
                      <span className="funil-conversao-total">({t.total})</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="funil-conversao-vazio">Sem transições no período.</p>
              )}
            </div>
          </div>
        </div>

        {/* Resumo Financeiro */}
        <div className="dashboard-card financeiro-card">
          <h2>Resumo Financeiro</h2>
          <div className="financeiro-grid">
            <div className="financeiro-grafico">
              <Line 
                data={financeiroData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)'
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  }
                }}
              />
            </div>
            <div className="financeiro-info">
              <div className="financeiro-item">
                <span className="financeiro-label">Receita Mensal</span>
                <span className="financeiro-valor">{formatarMoeda(resumoFinanceiro.receitaMensal)}</span>
              </div>
              <div className="financeiro-item">
                <span className="financeiro-label">Receita Anual</span>
                <span className="financeiro-valor">{formatarMoeda(resumoFinanceiro.receitaAnual)}</span>
              </div>
              <div className="financeiro-item">
                <span className="financeiro-label">Previsão do Mês</span>
                <span className="financeiro-valor">{formatarMoeda(resumoFinanceiro.previsaoMes)}</span>
              </div>
              <div className="financeiro-item">
                <span className="financeiro-label">A Receber</span>
                <span className="financeiro-valor">{formatarMoeda(resumoFinanceiro.contasReceber)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Atualizações de Marcas */}
        <div className="dashboard-card atualizacoes-card">
          <h2>Últimas Atualizações de Marcas</h2>
          <div className="atualizacoes-lista">
            {atualizacoesMarcas.length === 0 && (
              <p className="atualizacoes-vazio">Sem atualizações.</p>
            )}
            {atualizacoesMarcas.map(atualizacao => {
              const podeAbrir = Boolean(onNavegar && atualizacao.clienteId);
              const abrir = () => onNavegar('detalhes-cliente', { clienteId: atualizacao.clienteId });
              return (
                <div
                  key={atualizacao.id}
                  className={`atualizacao-item${podeAbrir ? ' atualizacao-item--clicavel' : ''}`}
                  onClick={podeAbrir ? abrir : undefined}
                  onKeyDown={podeAbrir ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      abrir();
                    }
                  } : undefined}
                  role={podeAbrir ? 'button' : undefined}
                  tabIndex={podeAbrir ? 0 : undefined}
                >
                  <div className="atualizacao-icone">
                    <IconeAtualizacao tipo={atualizacao.tipo} />
                  </div>
                  <div className="atualizacao-conteudo">
                    <div className="atualizacao-header">
                      <h3 className="atualizacao-titulo">{atualizacao.marca}</h3>
                      <span className="atualizacao-data">{formatarData(atualizacao.data)}</span>
                    </div>
                    <p className="atualizacao-descricao">{atualizacao.atualizacao}</p>
                    <span className={`atualizacao-status ${classeStatusAtualizacao(atualizacao.status)}`}>
                      {rotuloStatusAtualizacao(atualizacao.status)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 